/**
 * Canonical brand-color palette per supported programming language.
 *
 * Keys MUST be the same canonical identifiers returned by
 * `normalizeLanguageIconKey` from `src/components/ui/IconRenderer.js` so we share
 * one source of truth for language identification across the app.
 *
 * Values are hex strings tuned to read on the dark theme (saturated but not
 * neon). Where the official brand color is too dim against a near-black
 * background (e.g. raw Python blue), we use a slightly lifted variant.
 */
import { normalizeLanguageIconKey } from '../components/ui/IconRenderer';

const LANGUAGE_BRAND_COLORS = Object.freeze({
  javascript: '#F7DF1E',
  typescript: '#3178C6',
  python: '#4B8BBE',
  java: '#ED8B00',
  csharp: '#9B7BD4',
  cpp: '#5C8FCC',
  c: '#A8B9CC',
  go: '#00ADD8',
  rust: '#DEA584',
  ruby: '#CC342D',
  kotlin: '#7F52FF',
  swift: '#FA7343',
  php: '#8993BE',
  html: '#E34F26',
  css: '#1572B6',
  bash: '#7BC97F',
  shell: '#7BC97F',
  sql: '#E48E00',
  r: '#276DC3',
  scala: '#DC322F',
  dart: '#0175C2',
  lua: '#5A8DD6',
  elixir: '#A074C4',
  haskell: '#8F7FBF',
  clojure: '#5881D8',
  perl: '#5A6FA8'
});

/**
 * Default fallback color when the language is missing or unrecognised.
 * Mirrors the existing roadmap "active" accent so behaviour is unchanged for
 * unmapped languages.
 */
export const DEFAULT_LANGUAGE_BRAND_COLOR = '#378ADD';

/**
 * Resolve a language identifier (any casing/alias supported by
 * `normalizeLanguageIconKey`) to a brand hex color.
 *
 * @param {string|null|undefined} language Raw language string (e.g. "JavaScript", "js", "PYTHON3").
 * @returns {string} Hex color string. Falls back to {@link DEFAULT_LANGUAGE_BRAND_COLOR}.
 */
export function getLanguageBrandColor(language) {
  const key = normalizeLanguageIconKey(language);
  if (key && LANGUAGE_BRAND_COLORS[key]) return LANGUAGE_BRAND_COLORS[key];
  return DEFAULT_LANGUAGE_BRAND_COLOR;
}
